import {
  CardWithSelector,
  Container,
  GridContainer,
  Heading,
  Link,
  Text,
} from "@fluidattacks/design";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";

import { AddPaymentMethod } from "..";
import { UpgradedPlanName } from "gql/graphql";
import type { IPaymentMethodAttr } from "pages/organization/billing/types";

interface IFreeTrialPaymentMethodsProps {
  country: string | null;
  onUpdate: () => void;
  organizationId: string;
  isOnTrial: boolean;
  closeAddModal: () => void;
  paymentMethods: IPaymentMethodAttr[];
}

const PDF_URL =
  "https://res.cloudinary.com/fluid-attacks/image/upload/fl_attachment:Fluid-Attacks-Plans/v1743522883/integrates/plans/Fluid_Attacks-Plans_and_pricing_2025.pdf";

export const FreeTrialPaymentMethods = ({
  country,
  onUpdate,
  organizationId,
  isOnTrial,
  closeAddModal,
  paymentMethods,
}: IFreeTrialPaymentMethodsProps): JSX.Element => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [typeOfPlan, setTypeOfPlan] = useState(UpgradedPlanName.Essential);

  const cardSelector = [
    {
      defaultChecked: true,
      description: (
        <Container>
          <Text color={theme.palette.gray[400]} fontWeight={"bold"} size={"md"}>
            {t("organization.billing.planInformation.essential.title")}
          </Text>
          <Text size={"md"}>
            {t("organization.billing.planInformation.essential.description")}
          </Text>
        </Container>
      ),
      id: "EssentialPlan",
      name: "typeOfPlan",
      title: t("autoenrollment.languages.essentialLanguages.essentialPlan"),
      value: UpgradedPlanName.Essential,
    },
    {
      description: (
        <Container>
          <Text color={theme.palette.gray[400]} fontWeight={"bold"} size={"md"}>
            {t("organization.billing.planInformation.advanced.title")}
          </Text>
          <Text size={"md"}>
            {t("organization.billing.planInformation.advanced.description")}
          </Text>
        </Container>
      ),
      id: "AdvancedPlan",
      imageId: "integrates/plans/advanced-plan-tag",
      isATag: true,
      name: "typeOfPlan",
      title: t("autoenrollment.languages.advancedLanguages.advancedPlan"),
      value: UpgradedPlanName.Advanced,
    },
  ];
  const handleCardClick = useCallback((cardValue: UpgradedPlanName): void => {
    setTypeOfPlan(cardValue);
  }, []);

  const handleCardClickWrapper = useCallback(
    (cardValue: UpgradedPlanName): (() => void) => {
      return (): void => {
        handleCardClick(cardValue);
      };
    },
    [handleCardClick],
  );

  return (
    <GridContainer gap={0} lg={2} md={2} sm={2} xl={2}>
      <Container
        display={"flex"}
        flexDirection={"column"}
        gap={0.75}
        height={"100%"}
        minHeight={"570px"}
        pt={1.25}
        px={1.25}
      >
        <Heading size={"md"}>{t("organization.billing.upgradePlan")}</Heading>
        <Text size={"md"}>
          {t("organization.billing.description")}{" "}
          <Link download={PDF_URL} href={PDF_URL} variant={"highRelevance"}>
            {t("app.link")}
            {"."}
          </Link>
        </Text>
        {cardSelector.map(
          (card): JSX.Element => (
            <CardWithSelector
              bgColor={"white"}
              customDescription={card.description}
              imageId={card.imageId}
              isATag={card.isATag}
              key={card.id}
              onClick={handleCardClickWrapper(card.value)}
              selectorProps={{
                defaultChecked: card.defaultChecked,
                name: card.name,
                value: card.value,
              }}
              title={card.title}
              width={"100%"}
            />
          ),
        )}
      </Container>
      <Container
        bgColor={"white"}
        borderBottom={`1px solid ${theme.palette.gray[300]}`}
        borderLeft={`1px solid ${theme.palette.gray[300]}`}
        width={"100%"}
      >
        <Heading mt={1.5} mx={1.5} size={"md"}>
          {t("organization.billing.cardInformation.enterPayments")}
        </Heading>
        <AddPaymentMethod
          country={country}
          isOnTrial={isOnTrial}
          onClose={closeAddModal}
          onUpdate={onUpdate}
          organizationId={organizationId}
          paymentMethods={paymentMethods}
          typeOfPlan={typeOfPlan}
        />
      </Container>
    </GridContainer>
  );
};
